import { useEffect, useMemo, useRef, useState } from 'react';
import type { NewsItem, NewsStatus, NewsCategory } from '../types';
import { listNews, approveNews, rejectNews, updateNews } from '../lib/api';
import { IconCheck, IconClose, IconRefresh, IconPlus } from '../components/icons';
import { relativeDate } from '../lib/format';
import { Skeleton } from '../components/Skeleton';
import { useToast } from '../lib/toast';

const NEWS_WRITER_LABEL = 'Sonnet 4.6';
const HIGH_SCORE_THRESHOLD = 8;

function wordCount(text: string | undefined): number {
  if (!text) return 0;
  return text.trim().split(/\s+/).filter(Boolean).length;
}

type Tab = NewsStatus | 'all';

const CATEGORIES: NewsCategory[] = ['Funding', 'Hardware', 'Software', 'M&A', 'Policy'];

/**
 * Optional callbacks pour remonter des counts au parent (App.tsx hydrate
 * draftNewsCount pour la BottomNav mobile). Ajouté 2026-05-28 (audit B2).
 */
export interface NewsPageProps {
  onDraftCountChange?: (n: number) => void;
}

export function NewsPage({ onDraftCountChange }: NewsPageProps = {}) {
  const [tab, setTab] = useState<Tab>('draft');
  const [items, setItems] = useState<NewsItem[]>([]);
  const [counts, setCounts] = useState({ draft: 0, published: 0, rejected: 0, all: 0 });
  const [fundsById, setFundsById] = useState<Record<string, string>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [editing, setEditing] = useState<NewsItem | null>(null);
  const [pendingId, setPendingId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());
  const [bulkRunning, setBulkRunning] = useState(false);
  const toast = useToast();

  // Remonte le compteur de drafts à chaque changement (badge mobile BottomNav).
  useEffect(() => { onDraftCountChange?.(counts.draft); }, [counts.draft, onDraftCountChange]);

  async function refresh(currentTab: Tab = tab) {
    setLoading(true);
    setError(null);
    setSelectedIds(new Set());
    try {
      const res = await listNews(currentTab);
      setItems(res.items);
      setCounts(res.counts);
      setFundsById(res.fundsById ?? {});
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load news');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(tab); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [tab]);

  async function onApprove(item: NewsItem) {
    setPendingId(item.id);
    try {
      await approveNews(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setCounts(c => ({ ...c, draft: Math.max(0, c.draft - 1), published: c.published + 1 }));
      toast.success('Article approved', item.title_en || item.title_fr || '(untitled)');
    } catch (e) {
      toast.error('Approve failed', e instanceof Error ? e.message : String(e));
    } finally {
      setPendingId(null);
    }
  }
  async function onReject(item: NewsItem) {
    setPendingId(item.id);
    try {
      await rejectNews(item.id);
      setItems(prev => prev.filter(i => i.id !== item.id));
      setCounts(c => ({ ...c, draft: Math.max(0, c.draft - 1), rejected: c.rejected + 1 }));
      toast.success('Article rejected', item.title_en || item.title_fr || '(untitled)');
    } catch (e) {
      toast.error('Reject failed', e instanceof Error ? e.message : String(e));
    } finally {
      setPendingId(null);
    }
  }
  function onSaved(updated: NewsItem) {
    setItems(prev => prev.map(i => (i.id === updated.id ? updated : i)));
    setEditing(null);
    toast.success('Changes saved');
  }

  function toggleSelect(id: string) {
    setSelectedIds(prev => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function selectAllVisible() {
    setSelectedIds(new Set(items.map(i => i.id)));
  }
  function clearSelection() {
    setSelectedIds(new Set());
  }
  function selectHighScore() {
    setSelectedIds(new Set(items.filter(i => typeof i.llm_score === 'number' && i.llm_score >= HIGH_SCORE_THRESHOLD).map(i => i.id)));
  }
  async function runBulk(action: 'approve' | 'reject') {
    if (!selectedIds.size || bulkRunning) return;
    setBulkRunning(true);
    const ids = Array.from(selectedIds);
    let ok = 0;
    let failed = 0;
    // Run sequentially to avoid hammering Airtable's 5 req/s rate limit.
    for (const id of ids) {
      try {
        if (action === 'approve') await approveNews(id);
        else await rejectNews(id);
        ok += 1;
      } catch {
        failed += 1;
      }
    }
    setItems(prev => prev.filter(i => !selectedIds.has(i.id)));
    setCounts(c => ({
      ...c,
      draft: Math.max(0, c.draft - ok),
      [action === 'approve' ? 'published' : 'rejected']: c[action === 'approve' ? 'published' : 'rejected'] + ok,
    }));
    setSelectedIds(new Set());
    setBulkRunning(false);
    if (failed) toast.error(`${action === 'approve' ? 'Approved' : 'Rejected'} ${ok} of ${ids.length}`, `${failed} failed`);
    else toast.success(`${ok} article${ok > 1 ? 's' : ''} ${action === 'approve' ? 'approved' : 'rejected'}`);
  }

  const highScoreCount = useMemo(
    () => items.filter(i => typeof i.llm_score === 'number' && i.llm_score >= HIGH_SCORE_THRESHOLD).length,
    [items],
  );
  const bulkable = tab === 'draft' || tab === 'all';

  return (
    <section className="px-4 md:px-8 py-5 md:py-7 max-w-[1200px]">
      <div className="flex items-start md:items-end justify-between mb-4 md:mb-5 gap-3">
        <div className="min-w-0">
          <h1 className="h-display text-[22px] md:text-[26px]">News moderation</h1>
          <p className="text-[12.5px] md:text-[13.5px] text-ink-muted mt-0.5 hidden sm:block">
            Auto-collected daily by the news cron. Approvals appear on financeforquantum.com within 30 min, no rebuild needed.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => refresh(tab)} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint flex items-center gap-1.5">
            <IconRefresh className={'w-4 h-4 text-ink-muted ' + (loading ? 'animate-spin' : '')} />
            Refresh
          </button>
          <button
            disabled
            title="Coming in V2"
            className="h-9 px-3 text-[13px] rounded-md border border-line bg-white text-ink-faint flex items-center gap-1.5 opacity-60 cursor-not-allowed"
          >
            <IconPlus className="w-4 h-4 text-ink-faint" />
            Manual entry
          </button>
        </div>
      </div>

      <div className="flex items-center gap-4 md:gap-6 border-b border-line mb-5 text-[13px] md:text-[13.5px] overflow-x-auto scroll-area">
        <TabBtn active={tab === 'draft'} onClick={() => setTab('draft')} label="Pending" badge={counts.draft} accent="amber" />
        <TabBtn active={tab === 'published'} onClick={() => setTab('published')} label="Approved" badge={counts.published} accent="muted" />
        <TabBtn active={tab === 'rejected'} onClick={() => setTab('rejected')} label="Rejected" badge={counts.rejected} accent="muted" />
        <TabBtn active={tab === 'all'} onClick={() => setTab('all')} label="All" badge={counts.all} accent="muted" />
      </div>

      {error && (
        <div className="mb-4 px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[13px]">{error}</div>
      )}

      {/* Bulk action helper: only meaningful when there's at least one draft to act on. */}
      {bulkable && items.length > 0 && (
        <div className="flex items-center gap-2 mb-3 text-[12.5px]">
          <button onClick={selectAllVisible} className="px-2.5 py-1 rounded-md border border-line bg-white hover:bg-cream-tint">Select all ({items.length})</button>
          {highScoreCount > 0 && (
            <button onClick={selectHighScore} className="px-2.5 py-1 rounded-md border border-funds-deep text-funds-deep bg-funds-soft hover:bg-funds-soft/80">
              Select high score ≥ {HIGH_SCORE_THRESHOLD} ({highScoreCount})
            </button>
          )}
          {selectedIds.size > 0 && (
            <span className="text-ink-muted ml-1">{selectedIds.size} selected</span>
          )}
        </div>
      )}

      <div className="space-y-3">
        {loading && items.length === 0 && (
          <>
            {Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="bg-white border border-line rounded-lg shadow-card p-5 space-y-3">
                <Skeleton className="h-3 w-48" />
                <Skeleton className="h-5 w-3/4" />
                <Skeleton className="h-3 w-full" />
                <Skeleton className="h-3 w-5/6" />
              </div>
            ))}
          </>
        )}
        {!loading && items.length === 0 && (
          <div className="bg-white border border-line rounded-lg p-8 text-center text-ink-muted text-[13.5px]">
            {tab === 'draft' && 'No pending articles. The news cron runs daily at 05:00 UTC.'}
            {tab === 'published' && 'No published articles yet.'}
            {tab === 'rejected' && 'No rejected articles.'}
            {tab === 'all' && 'No articles in the table.'}
          </div>
        )}
        {items.map(item => (
          <NewsCard
            key={item.id}
            item={item}
            fundsById={fundsById}
            pending={pendingId === item.id}
            selected={selectedIds.has(item.id)}
            selectable={bulkable}
            onToggleSelect={() => toggleSelect(item.id)}
            onApprove={() => onApprove(item)}
            onReject={() => onReject(item)}
            onEdit={() => setEditing(item)}
          />
        ))}
      </div>

      {/* Floating bulk action bar */}
      {selectedIds.size > 0 && (
        <div className="fixed bottom-6 left-1/2 -translate-x-1/2 z-30 bg-navy text-white rounded-full shadow-pop pl-5 pr-2 py-2 flex items-center gap-3 border border-navy-soft">
          <span className="text-[13px] font-medium num">{selectedIds.size} selected</span>
          <span className="h-4 w-px bg-white/20" />
          <button
            onClick={() => runBulk('approve')}
            disabled={bulkRunning}
            className="h-8 px-3 rounded-full bg-funds hover:bg-funds-deep text-white text-[12.5px] font-medium flex items-center gap-1.5 disabled:opacity-60"
          >
            {bulkRunning ? <IconRefresh className="w-3.5 h-3.5 animate-spin" /> : <IconCheck className="w-3.5 h-3.5" />}
            Approve
          </button>
          <button
            onClick={() => runBulk('reject')}
            disabled={bulkRunning}
            className="h-8 px-3 rounded-full bg-white/10 hover:bg-white/20 text-white text-[12.5px] font-medium disabled:opacity-60"
          >
            Reject
          </button>
          <button onClick={clearSelection} disabled={bulkRunning} className="h-8 w-8 grid place-items-center rounded-full hover:bg-white/10 text-white/80">
            <IconClose className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {editing && (
        <NewsEditDrawer
          item={editing}
          onClose={() => setEditing(null)}
          onSaved={onSaved}
        />
      )}
    </section>
  );
}

/* ---------- Tab button ---------- */

function TabBtn({ active, onClick, label, badge, accent }: { active: boolean; onClick: () => void; label: string; badge: number; accent: 'amber' | 'muted' }) {
  return (
    <button
      onClick={onClick}
      className={'px-1 py-3 border-b-2 -mb-px flex items-center gap-2 ' + (active ? 'border-navy text-ink font-medium' : 'border-transparent text-ink-muted hover:text-ink')}
    >
      {label}
      <span className={'num text-[11px] font-medium px-1.5 py-px rounded-full ' + (accent === 'amber' && badge > 0 ? 'bg-amber-soft text-amber' : 'text-ink-faint')}>{badge}</span>
    </button>
  );
}

/* ---------- News card ---------- */

function NewsCard({ item, fundsById, pending, selected, selectable, onToggleSelect, onApprove, onReject, onEdit }: {
  item: NewsItem;
  fundsById: Record<string, string>;
  pending: boolean;
  selected: boolean;
  selectable: boolean;
  onToggleSelect: () => void;
  onApprove: () => void;
  onReject: () => void;
  onEdit: () => void;
}) {
  // Swipe-to-action: drag card horizontally on touch devices.
  // Right >100px = approve, left >100px = reject. Vertical drag passes
  // through (so the page still scrolls naturally).
  const [dx, setDx] = useState(0);
  const [snap, setSnap] = useState(false);
  const startX = useRef(0);
  const startY = useRef(0);
  const horiz = useRef<boolean | null>(null);
  const swipeable = item.status === 'draft' && !pending;

  function onTouchStart(e: React.TouchEvent) {
    if (!swipeable) return;
    setSnap(false);
    startX.current = e.touches[0].clientX;
    startY.current = e.touches[0].clientY;
    horiz.current = null;
  }
  function onTouchMove(e: React.TouchEvent) {
    if (!swipeable || !startX.current) return;
    const dxRaw = e.touches[0].clientX - startX.current;
    const dyRaw = e.touches[0].clientY - startY.current;
    if (horiz.current === null) {
      // Decide once whether this gesture is horizontal or vertical, after
      // moving past a small threshold.
      if (Math.abs(dxRaw) > 10 || Math.abs(dyRaw) > 10) horiz.current = Math.abs(dxRaw) > Math.abs(dyRaw);
    }
    if (horiz.current) {
      // Apply slight rubber-band beyond ±200px so it never feels stuck.
      const clamped = Math.max(-260, Math.min(260, dxRaw));
      setDx(clamped);
    }
  }
  function onTouchEnd() {
    if (!swipeable) return;
    setSnap(true);
    if (Math.abs(dx) > 100) {
      // Animate off-screen then trigger the action.
      setDx(dx > 0 ? 360 : -360);
      window.setTimeout(() => {
        if (dx > 0) onApprove();
        else onReject();
        setDx(0);
        setSnap(false);
      }, 200);
    } else {
      setDx(0);
      window.setTimeout(() => setSnap(false), 250);
    }
    startX.current = 0;
    startY.current = 0;
    horiz.current = null;
  }

  const swipeRevealOpacity = Math.min(1, Math.abs(dx) / 100);
  const swipeBg = dx > 0 ? 'bg-funds' : 'bg-red-500';
  const swipeLabel = dx > 0 ? 'Approve' : 'Reject';
  const swipeAlign = dx > 0 ? 'justify-start' : 'justify-end';
  const title = item.title_en || item.title_fr || '(untitled)';
  const subtitle = item.subtitle_en || item.subtitle_fr || '';
  const firstSource = item.sources?.[0];

  // Resolve linked_funds recIds to display names via the map returned by the
  // backend. If unresolved, skip rather than show raw recIds.
  const linkedNames = (item.linked_funds || [])
    .map(id => fundsById[id])
    .filter(Boolean) as string[];

  // Word count of the body the moderator is most likely reviewing (EN by
  // default, FR when EN is empty). Matches the mockup's "220 words".
  const bodyForCount = item.body_en || item.body_fr;
  const words = wordCount(bodyForCount);

  return (
    <div className="relative rounded-lg overflow-hidden">
      {/* Swipe reveal background (mobile only): green=approve right, red=reject left */}
      {swipeable && Math.abs(dx) > 4 && (
        <div className={'md:hidden absolute inset-0 flex items-center px-6 text-white font-medium text-[14px] ' + swipeBg + ' ' + swipeAlign} style={{ opacity: swipeRevealOpacity }}>
          <span>{swipeLabel}</span>
        </div>
      )}

      <article
        onTouchStart={onTouchStart}
        onTouchMove={onTouchMove}
        onTouchEnd={onTouchEnd}
        style={{ transform: `translateX(${dx}px)`, transition: snap ? 'transform .22s cubic-bezier(.2,.8,.2,1)' : 'none' }}
        className={'bg-white border rounded-lg shadow-card p-5 md:p-5 transition ' + (selected ? 'border-navy ring-1 ring-navy/20' : 'border-line')}
      >
      <div className="flex items-start justify-between gap-4 md:gap-6">
        {selectable && (
          <input
            type="checkbox"
            checked={selected}
            onChange={onToggleSelect}
            className="mt-1.5 w-5 h-5 md:w-4 md:h-4 rounded border-line text-navy focus:ring-navy/20 shrink-0"
            aria-label="Select for bulk action"
          />
        )}
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2 text-[11.5px] text-ink-muted mb-1.5 flex-wrap">
            {linkedNames.length > 0 && (
              <span className="font-medium text-funds-deep">{linkedNames.join(', ')}</span>
            )}
            {linkedNames.length > 0 && firstSource?.publisher && <span>·</span>}
            {firstSource?.publisher && <span>{firstSource.publisher}</span>}
            {item.published_at && <><span>·</span><span>{item.published_at.slice(0, 10)}</span></>}
            {item.category && (
              <>
                <span>·</span>
                <span className="inline-flex items-center px-2 py-px rounded-full text-[11px] font-medium bg-cream-deep text-navy">{item.category}</span>
              </>
            )}
            {typeof item.llm_score === 'number' && (
              <>
                <span>·</span>
                <span className={'inline-flex items-center gap-1 px-2 py-px rounded-full text-[11px] font-medium ' + (item.llm_score >= 7 ? 'bg-funds-soft text-funds-deep' : 'bg-amber-soft text-amber')}>
                  score {item.llm_score.toFixed(1)}
                </span>
              </>
            )}
            {item.status === 'published' && (
              <span className="inline-flex items-center gap-1 px-2 py-px rounded-full text-[11px] font-medium bg-funds-soft text-funds-deep">approved</span>
            )}
            {item.status === 'rejected' && (
              <span className="inline-flex items-center gap-1 px-2 py-px rounded-full text-[11px] font-medium bg-red-50 text-red-700">rejected</span>
            )}
          </div>
          <h3 className="h-display text-[17px] leading-snug mb-1.5">{title}</h3>
          {subtitle && <p className="text-[13px] text-ink-muted leading-relaxed mb-2.5">{subtitle}</p>}
          <div className="flex items-center gap-3 text-[12.5px] flex-wrap">
            {firstSource?.url && (
              <a className="text-navy underline underline-offset-4 decoration-line break-all" href={firstSource.url} target="_blank" rel="noreferrer">
                {firstSource.url.replace(/^https?:\/\/(www\.)?/, '').slice(0, 80)}
              </a>
            )}
            {firstSource?.url && words > 0 && <span className="text-ink-faint">·</span>}
            {words > 0 && (
              <span className="text-ink-muted">written by {NEWS_WRITER_LABEL} · <span className="num">{words}</span> words</span>
            )}
          </div>
        </div>

        <div className="hidden md:flex flex-col gap-2 shrink-0 w-[140px]">
          {item.status === 'draft' && (
            <button
              onClick={onApprove}
              disabled={pending}
              className="h-9 text-[13px] rounded-md bg-funds text-white hover:bg-funds-deep flex items-center justify-center gap-1.5 disabled:opacity-60"
            >
              {pending ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
              Approve
            </button>
          )}
          <button onClick={onEdit} disabled={pending} className="h-9 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint disabled:opacity-60">Edit</button>
          {item.status !== 'rejected' && (
            <button
              onClick={onReject}
              disabled={pending}
              className="h-9 text-[13px] rounded-md text-red-700 hover:bg-red-50 border border-line disabled:opacity-60"
            >
              {item.status === 'published' ? 'Unpublish' : 'Reject'}
            </button>
          )}
        </div>
      </div>

      {/* Mobile action row: full-width buttons, swipe hint on draft cards */}
      <div className="md:hidden mt-3 flex gap-2">
        {item.status === 'draft' && (
          <button
            onClick={onApprove}
            disabled={pending}
            className="flex-1 h-11 text-[13.5px] rounded-md bg-funds text-white active:bg-funds-deep flex items-center justify-center gap-1.5 disabled:opacity-60 active:scale-[.98] transition"
          >
            {pending ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
            Approve
          </button>
        )}
        <button onClick={onEdit} disabled={pending} className="flex-1 h-11 text-[13.5px] rounded-md border border-line bg-white active:bg-cream-tint disabled:opacity-60">Edit</button>
        {item.status !== 'rejected' && (
          <button
            onClick={onReject}
            disabled={pending}
            className="flex-1 h-11 text-[13.5px] rounded-md text-red-700 active:bg-red-50 border border-line disabled:opacity-60"
          >
            {item.status === 'published' ? 'Unpublish' : 'Reject'}
          </button>
        )}
      </div>
      {item.status === 'draft' && (
        <div className="md:hidden mt-2 text-[11px] text-ink-faint text-center">Swipe right to approve, left to reject</div>
      )}
      </article>
    </div>
  );
}

/* ---------- Edit drawer ---------- */

function NewsEditDrawer({ item, onClose, onSaved }: { item: NewsItem; onClose: () => void; onSaved: (i: NewsItem) => void }) {
  const [draft, setDraft] = useState<NewsItem>(item);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [open, setOpen] = useState(false);
  const [lang, setLang] = useState<'en' | 'fr'>('en');

  useEffect(() => {
    const t = setTimeout(() => setOpen(true), 10);
    return () => clearTimeout(t);
  }, []);
  useEffect(() => {
    function onKey(e: KeyboardEvent) { if (e.key === 'Escape') close(); }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  function close() { setOpen(false); setTimeout(onClose, 220); }

  const dirty = useMemo(() => JSON.stringify(draft) !== JSON.stringify(item), [draft, item]);

  function set<K extends keyof NewsItem>(key: K, val: NewsItem[K]) {
    setDraft(prev => ({ ...prev, [key]: val }));
  }

  async function onSave() {
    if (!dirty || saving) return;
    setSaving(true);
    setError(null);
    try {
      // Send only the fields the user actually edited to keep the PATCH payload tight.
      const diff: Partial<NewsItem> = {};
      const keys: (keyof NewsItem)[] = [
        'status', 'slug', 'category', 'published_at',
        'title_en', 'title_fr', 'subtitle_en', 'subtitle_fr',
        'body_en', 'body_fr', 'hero_image_url', 'hero_image_credit',
      ];
      for (const k of keys) {
        if (JSON.stringify(item[k]) !== JSON.stringify(draft[k])) (diff as any)[k] = draft[k];
      }
      const res = await updateNews(item.id, diff);
      onSaved(res.item);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  }

  return (
    <>
      <div className={'fixed inset-0 bg-navy/30 backdrop z-30 ' + (open ? 'open' : '')} onClick={close} />
      <aside className={'drawer fixed top-0 right-0 z-40 h-full w-[640px] max-w-[92vw] bg-white shadow-drawer flex flex-col ' + (open ? 'open' : '')}>
        <header className="px-6 py-4 border-b border-line-soft flex items-center justify-between gap-4">
          <div className="min-w-0">
            <div className="h-display text-[16px] leading-tight truncate">{draft.title_en || draft.title_fr || '(untitled)'}</div>
            <div className="text-[11.5px] text-ink-muted leading-tight font-mono">{item.id}</div>
          </div>
          <button onClick={close} className="h-8 w-8 grid place-items-center rounded-md hover:bg-cream-tint" title="Close (Esc)">
            <IconClose className="w-4 h-4 text-ink-muted" />
          </button>
        </header>

        <div className="px-6 py-3 border-b border-line-soft flex items-center justify-between">
          <div className="inline-flex border border-line rounded-md bg-white p-0.5 text-[12px]">
            <button onClick={() => setLang('en')} className={'px-2.5 py-1 rounded ' + (lang === 'en' ? 'bg-cream-tint text-ink font-medium' : 'text-ink-muted')}>English</button>
            <button onClick={() => setLang('fr')} className={'px-2.5 py-1 rounded ' + (lang === 'fr' ? 'bg-cream-tint text-ink font-medium' : 'text-ink-muted')}>Français</button>
          </div>
          <select
            value={draft.status}
            onChange={e => set('status', e.target.value as NewsStatus)}
            className="h-8 px-2.5 text-[12.5px] rounded-md border border-line bg-white"
          >
            <option value="draft">draft</option>
            <option value="published">published</option>
            <option value="rejected">rejected</option>
          </select>
        </div>

        <div className="flex-1 overflow-y-auto scroll-area px-6 py-5 space-y-4">
          {error && (
            <div className="px-3 py-2 rounded-md bg-red-50 border border-red-200 text-red-800 text-[12.5px]">{error}</div>
          )}

          <Field label="Category">
            <select value={draft.category || ''} onChange={e => set('category', e.target.value as NewsCategory)} className="w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white">
              <option value="">Select…</option>
              {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
            </select>
          </Field>

          <Field label="Slug (URL kebab-case)">
            <input
              value={draft.slug}
              onChange={e => set('slug', e.target.value)}
              className="w-full h-9 px-2.5 text-[13.5px] font-mono rounded-md border border-line bg-white ring-input"
            />
          </Field>

          <Field label={`Title (${lang.toUpperCase()})`}>
            <input
              value={lang === 'en' ? draft.title_en : draft.title_fr}
              onChange={e => set(lang === 'en' ? 'title_en' : 'title_fr', e.target.value)}
              className="w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white ring-input"
            />
          </Field>

          <Field label={`Subtitle (${lang.toUpperCase()})`}>
            <input
              value={lang === 'en' ? draft.subtitle_en : draft.subtitle_fr}
              onChange={e => set(lang === 'en' ? 'subtitle_en' : 'subtitle_fr', e.target.value)}
              className="w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white ring-input"
            />
          </Field>

          <Field label={`Body (${lang.toUpperCase()})`}>
            <textarea
              value={lang === 'en' ? draft.body_en : draft.body_fr}
              onChange={e => set(lang === 'en' ? 'body_en' : 'body_fr', e.target.value)}
              rows={10}
              className="w-full text-[13px] border border-line rounded-md p-2.5 ring-input resize-y"
            />
          </Field>

          <Field label="Hero image URL">
            <input
              value={draft.hero_image_url}
              onChange={e => set('hero_image_url', e.target.value)}
              className="w-full h-9 px-2.5 text-[13.5px] font-mono rounded-md border border-line bg-white ring-input"
            />
          </Field>

          <Field label="Hero image credit">
            <input
              value={draft.hero_image_credit}
              onChange={e => set('hero_image_credit', e.target.value)}
              className="w-full h-9 px-2.5 text-[13.5px] rounded-md border border-line bg-white ring-input"
            />
          </Field>

          {item.sources?.length > 0 && (
            <Field label={`Sources (${item.sources.length})`}>
              <div className="space-y-1.5">
                {item.sources.map((s, i) => (
                  <div key={i} className="text-[12px] text-ink-muted bg-cream-tint border border-line-soft rounded-md px-2.5 py-1.5">
                    <div className="font-medium text-ink">{s.title || '(untitled)'}</div>
                    <a className="text-navy underline underline-offset-2 break-all font-mono text-[11px]" href={s.url} target="_blank" rel="noreferrer">{s.url}</a>
                    {s.publisher && <span className="ml-2">· {s.publisher}</span>}
                  </div>
                ))}
              </div>
            </Field>
          )}

          {item.llm_rationale && (
            <Field label="LLM rationale (read-only)">
              <div className="text-[12px] text-ink-muted bg-cream-tint border border-line-soft rounded-md px-3 py-2 whitespace-pre-wrap">{item.llm_rationale}</div>
            </Field>
          )}

          <div className="text-[11.5px] text-ink-faint pt-2">Created {relativeDate(item.createdTime)}</div>
        </div>

        <footer className="px-6 py-3.5 border-t border-line-soft bg-white flex items-center justify-between">
          <div className="text-[12.5px] text-ink-muted flex items-center gap-2">
            <span className={'inline-block w-1.5 h-1.5 rounded-full ' + (dirty ? 'bg-amber' : 'bg-funds')} />
            <span>{dirty ? 'Unsaved changes' : 'Up to date'}</span>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={close} className="h-9 px-3 text-[13px] rounded-md border border-line bg-white hover:bg-cream-tint">Cancel</button>
            <button onClick={onSave} disabled={!dirty || saving} className="h-9 px-3.5 text-[13px] rounded-md bg-navy text-white hover:bg-navy-soft font-medium flex items-center gap-1.5 disabled:opacity-50">
              {saving ? <IconRefresh className="w-4 h-4 animate-spin" /> : <IconCheck className="w-4 h-4" />}
              {saving ? 'Saving…' : 'Save'}
            </button>
          </div>
        </footer>
      </aside>
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <label className="block text-[11.5px] uppercase tracking-wider text-ink-muted mb-1">{label}</label>
      {children}
    </div>
  );
}
